import React, { useRef, useState } from 'react';
import { HiOutlineArrowsExpand, HiOutlineDownload, HiOutlinePaperClip } from 'react-icons/hi';
import PresentationModal from '../../../../../organisms/PresentationModal';
import { AttachmentDelivery } from '../../../../../../types/communication';
import { expirationLabel } from '../../../../../../utils/emailEnvelope';

export interface PreviewAttachment {
    name: string;
    delivery: AttachmentDelivery;
    expirationMinutes?: number;
}

interface EmailPreviewProps {
    from: string;
    to: string;
    subject: string;
    preheader: string;
    html: string;
    attachments: PreviewAttachment[];
}

const EmailFrame: React.FC<{ html: string; minHeight: number }> = ({ html, minHeight }) => {
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState(minHeight);
    return (
        <iframe
            ref={frameRef}
            title="Pré-visualização do e-mail"
            sandbox="allow-same-origin"
            srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0}</style></head><body>${html}</body></html>`}
            onLoad={() => {
                const doc = frameRef.current?.contentDocument;
                if (doc) setHeight(Math.max(minHeight, doc.body.scrollHeight + 8));
            }}
            className="w-full border-0 bg-[#F3F4F6]"
            style={{ height }}
        />
    );
};

const fileName = (value: string) => value.split('/').pop() || value;

const EmailPreview: React.FC<EmailPreviewProps> = ({ from, to, subject, preheader, html, attachments }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 bg-white">
                <div className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-full bg-teal-500 text-white text-sm font-semibold flex items-center justify-center shrink-0">A</span>
                    <div className="flex-1 min-w-0">
                        <div className="text-[15px] font-semibold text-gray-900 leading-snug break-words">{subject || 'Sem assunto'}</div>
                        <div className="text-xs text-gray-500 truncate">
                            <span className="text-gray-700">{from || 'remetente'}</span> para {to || 'destinatário'}
                        </div>
                        {preheader && <div className="text-xs text-gray-400 truncate mt-0.5">{preheader}</div>}
                    </div>
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="text-gray-400 hover:text-gray-700"
                        title="Ver em tamanho real"
                        aria-label="Ver em tamanho real"
                    >
                        <HiOutlineArrowsExpand size={18} />
                    </button>
                </div>
                {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 pl-12">
                        {attachments.map((a, index) => (
                            <span
                                key={index}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] max-w-full ${
                                    a.delivery === 'LINK' ? 'border-sky-200 bg-sky-50 text-sky-800' : 'border-gray-200 text-gray-600'
                                }`}
                                title={a.delivery === 'LINK' ? 'Link para download' : 'Anexo'}
                            >
                                {a.delivery === 'LINK' ? <HiOutlineDownload size={12} className="shrink-0" /> : <HiOutlinePaperClip size={12} className="shrink-0" />}
                                <span className="truncate">{fileName(a.name)}</span>
                                {a.delivery === 'LINK' && a.expirationMinutes && (
                                    <span className="opacity-70 shrink-0">· {expirationLabel(a.expirationMinutes)}</span>
                                )}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            <EmailFrame html={html} minHeight={360} />
            <PresentationModal
                id="email-preview-expanded"
                show={expanded}
                onClose={() => setExpanded(false)}
                header={subject || 'Sem assunto'}
                height="75vh"
            >
                <EmailFrame html={html} minHeight={600} />
            </PresentationModal>
        </div>
    );
};

export default EmailPreview;
